import classNames from 'classnames';
import React, { ChangeEvent, FC, useCallback, useEffect, useRef } from 'react';

import { IcCloseIcon, UnionIcon } from '@gem/icons';
import { AddKeyPressHandler } from '@gem/keyboard-handler';

import { Button } from '../Button/Button';
import { IconButton } from '../IconButton/IconButton';
import { OverlayingPopup } from '../OverlayingPopup/OverlayingPopup';
import styles from './Dialog.styles.m.css';

interface IDialogProps {
  title?: string;
  text?: string;
  primaryText: string;
  secondaryText?: string;
  isOpened?: boolean;
  checkBoxText?: string;
  isSelected?: boolean;
  isLoading?: boolean;
  hasCloseButton?: boolean;
  className?: string;
  popupClassName?: string;
  html?: string;
  primaryOnClick(): void;
  secondaryOnClick?(): void;
  onClose(): void;
  checkBoxChange?(event: ChangeEvent<HTMLInputElement>): void;
}

export const Dialog: FC<IDialogProps> = ({
  title,
  text,
  primaryText,
  primaryOnClick,
  secondaryText,
  secondaryOnClick,
  onClose,
  isOpened = true,
  checkBoxText,
  checkBoxChange,
  isSelected,
  isLoading = false,
  hasCloseButton = false,
  className,
  popupClassName,
  html,
}) => {
  const primaryButtonRef = useRef<HTMLButtonElement>(null);
  const secondaryButtonRef = useRef<HTMLButtonElement>(null);
  const containerClass = classNames(styles.dialogContainer, popupClassName, {
    [styles.hasCloseButton]: hasCloseButton,
    [styles.hasTitle]: !!title,
  });

  const checkboxClass = classNames(styles.checkboxView, {
    [styles.selected]: isSelected,
  });

  const focusSecondaryBtn = useCallback(() => {
    secondaryButtonRef.current?.focus();
  }, []);

  const focusPrimaryBtn = useCallback(() => {
    primaryButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    primaryButtonRef.current?.focus();
  }, []);

  return (
    <OverlayingPopup
      isOpened={isOpened}
      onClose={onClose}
      className={className}
      popupClassName={containerClass}
      isIgnoreClickOutside
    >
      <AddKeyPressHandler
        onEsc={onClose}
        onArrowLeft={focusSecondaryBtn}
        onArrowRight={focusPrimaryBtn}
      />
      {hasCloseButton && (
        <IconButton
          className={styles.closeButton}
          Icon={IcCloseIcon}
          onClick={onClose}
        />
      )}

      {title && <div className={styles.title}>{title}</div>}
      {text && (
        <div
          className={styles.text}
          data-test="dialog-text"
        >
          {text}
        </div>
      )}
      {html && (
        <div
          className={styles.text}
          data-test="dialog-text"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}
      {checkBoxText && checkBoxChange && (
        <label className={styles.checkbox}>
          <div className={checkboxClass}>
            <input
              className={styles.checkboxInput}
              type="checkbox"
              onChange={checkBoxChange}
              disabled={isLoading}
            />
            {isSelected && <UnionIcon />}
          </div>
          <span className={styles.checkboxText}>{checkBoxText}</span>
        </label>
      )}
      <div className={styles.actionPanel}>
        {secondaryText && (
          <Button
            setRef={secondaryButtonRef}
            className={styles.secondaryButton}
            onClick={secondaryOnClick}
            isDisabled={isLoading}
            dataTest="secondary-button"
            theme="text-secondary"
          >
            {secondaryText}
          </Button>
        )}
        <Button
          setRef={primaryButtonRef}
          onClick={primaryOnClick}
          className={styles.button}
          isLoading={isLoading}
          dataTest="primary-button"
          theme="primary"
        >
          {primaryText}
        </Button>
      </div>
    </OverlayingPopup>
  );
};
